import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  vendorPortalApi,
  downloadVendorFile,
  type OpenRequest,
  type OpenRequestItem,
  type VendorSubmitErrorCode,
} from '../../features/vendorPortal/api.ts';
import { useVendorAuthStore } from '../../store/vendorAuth.ts';
import { useCountdown } from '../../lib/useCountdown.ts';
import { CountdownBadge } from '../../components/CountdownBadge.tsx';
import { Icon } from '../../components/Icon.tsx';
import { t } from '../../lib/strings.ts';
import { formatDate } from '@/lib/format';
import { Pagination } from '../../components/Pagination.tsx';

const PAGE_SIZE = 10;

function submitErrorMessage(code: VendorSubmitErrorCode | undefined): string {
  switch (code) {
    case 'deadline_passed': return t.vendorPortal.errDeadlinePassed;
    case 'already_submitted': return t.vendorPortal.errAlreadySubmitted;
    case 'request_closed': return t.vendorPortal.errRequestClosed;
    case 'declaration_required': return t.vendorPortal.errDeclarationRequired;
    case 'no_prices': return t.vendorPortal.errNoPrices;
    case 'invalid_lines':
    case 'invalid_price': return t.vendorPortal.errInvalidPrice;
    case 'not_found': return t.vendorPortal.errNotFound;
    default: return t.vendorPortal.errSubmitGeneral;
  }
}

interface LineState {
  noQuote: boolean;
  priceStr: string;
}

function initLines(items: OpenRequestItem[]): Record<string, LineState> {
  const out: Record<string, LineState> = {};
  for (const item of items) out[item.id] = { noQuote: false, priceStr: '' };
  return out;
}

function SubmitForm({ request, vendorName }: { request: OpenRequest; vendorName: string }): JSX.Element {
  const qc = useQueryClient();
  const cd = useCountdown(request.deadline);
  const [lines, setLines] = useState<Record<string, LineState>>(() => initLines(request.items));
  const [declared, setDeclared] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: (payload: Parameters<typeof vendorPortalApi.submit>[1]) =>
      vendorPortalApi.submit(request.requestId, payload),
    onSuccess: () => {
      setSubmitted(true);
      setSubmitError(null);
      void qc.invalidateQueries({ queryKey: ['vendor-open-requests'] });
      void qc.invalidateQueries({ queryKey: ['vendor-quotations'] });
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: string } } }).response?.data?.error as
        | VendorSubmitErrorCode
        | undefined;
      setSubmitError(submitErrorMessage(code));
    },
  });

  function setLine(itemId: string, patch: Partial<LineState>) {
    setLines((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } as LineState }));
  }

  function runningTotal(): number {
    return request.items.reduce((sum, item) => {
      const ls = lines[item.id];
      if (!ls || ls.noQuote) return sum;
      const price = parseFloat(ls.priceStr);
      if (isNaN(price) || price < 0) return sum;
      return sum + price * item.qty;
    }, 0);
  }

  function hasAtLeastOnePrice(): boolean {
    return Object.values(lines).some((ls) => {
      if (ls.noQuote) return false;
      const p = parseFloat(ls.priceStr);
      return !isNaN(p) && p >= 0;
    });
  }

  const canSubmit = hasAtLeastOnePrice() && declared && !cd.passed;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const submitLines = request.items.map((item) => {
      const ls = lines[item.id] ?? { noQuote: true, priceStr: '' };
      const price = parseFloat(ls.priceStr);
      return {
        requestItemId: item.id,
        unitPrice: ls.noQuote || isNaN(price) ? null : price,
      };
    });
    mutation.mutate({ confirmedDeclaration: true, lines: submitLines });
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <Icon name="check_circle" size={40} className="text-[hsl(var(--success))]" />
        <p className="text-sm font-medium text-ink">{t.vendorPortal.submitSuccess}</p>
      </div>
    );
  }

  const total = runningTotal();

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4 pt-2">
      <div className="space-y-3">
        {request.items.map((item, idx) => {
          const ls = lines[item.id] ?? { noQuote: false, priceStr: '' };
          return (
            <div key={item.id} className="card-paper p-3 space-y-3">
              <div className="flex gap-3 items-start">
                <span className="num text-muted-foreground mt-0.5 w-5 shrink-0 text-end">{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{item.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    <span className="num">{item.qty}</span>{' '}{item.unit}
                    {item.spec && <> — {item.spec}</>}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-0">
                  <label htmlFor={`price-${item.id}`} className="field-label mb-1 text-xs">
                    {t.vendorPortal.unitPriceMvr}
                  </label>
                  <input
                    id={`price-${item.id}`}
                    type="number"
                    min="0"
                    step="0.01"
                    className="input text-base num"
                    placeholder="0.00"
                    value={ls.priceStr}
                    disabled={ls.noQuote}
                    onChange={(e) => setLine(item.id, { priceStr: e.target.value })}
                    inputMode="decimal"
                    dir="ltr"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none pb-1 shrink-0">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded accent-primary cursor-pointer"
                    checked={ls.noQuote}
                    onChange={(e) =>
                      setLine(item.id, { noQuote: e.target.checked, priceStr: e.target.checked ? '' : ls.priceStr })
                    }
                  />
                  <span className="text-xs text-muted-foreground">{t.vendorPortal.noQuote}</span>
                </label>
              </div>

              {!ls.noQuote && ls.priceStr && !isNaN(parseFloat(ls.priceStr)) && (
                <p className="text-xs text-muted-foreground tabular text-start">
                  <span className="num">{item.qty}</span>
                  {' × MVR '}
                  <span className="num">{parseFloat(ls.priceStr).toFixed(2)}</span>
                  {' = '}
                  <strong className="text-foreground num">
                    {'MVR '}{(item.qty * parseFloat(ls.priceStr)).toFixed(2)}
                  </strong>
                </p>
              )}
            </div>
          );
        })}
      </div>

      {total > 0 && (
        <div className="card-paper p-3 flex justify-between items-center">
          <span className="text-sm font-medium">{t.vendorPortal.estimatedTotal}</span>
          <span className="font-bold num text-ink">{'MVR '}{total.toFixed(2)}</span>
        </div>
      )}

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5 w-5 h-5 rounded accent-primary cursor-pointer shrink-0"
          checked={declared}
          onChange={(e) => setDeclared(e.target.checked)}
        />
        <span className="text-sm leading-relaxed">{t.vendorPortal.declarationText(vendorName)}</span>
      </label>

      {submitError && (
        <div role="alert" className="rounded-2xl bg-[hsl(var(--error-container))] px-3.5 py-2.5 text-sm text-destructive">
          {submitError}
        </div>
      )}

      <button type="submit" disabled={!canSubmit || mutation.isPending} className="btn-primary w-full py-3 text-base">
        {mutation.isPending ? t.vendorPortal.submitting : t.vendorPortal.submit}
      </button>

      <p className="text-xs text-center text-muted-foreground">{t.vendorPortal.submitOnce}</p>
    </form>
  );
}

function RequestCard({ request, vendorName }: { request: OpenRequest; vendorName: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <article className="p-4 space-y-3 sm:px-5">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <p className="font-medium leading-snug text-ink">{request.title}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="mono-meta num">{request.refNumber}</span>
            <span className="inline-flex items-center gap-1.5">
              <Icon name="checklist" size={15} className="shrink-0 text-foreground/40" />
              {t.vendorPortal.itemsHeading(request.items.length)}
            </span>
            {request.deadline && (
              <span className="inline-flex items-center gap-1.5">
                <Icon name="calendar_month" size={15} className="shrink-0 text-foreground/40" />
                {t.vendorPortal.deadline} <span className="num">{formatDate(request.deadline)}</span>
              </span>
            )}
          </div>
        </div>
        <CountdownBadge deadline={request.deadline} className="shrink-0" />
      </div>

      {request.specDoc && (
        <button
          type="button"
          onClick={() => void downloadVendorFile(vendorPortalApi.specDocUrl(request.requestId), request.specDoc!.name)}
          className="flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <Icon name="description" size={16} className="shrink-0" />
          <span className="truncate" dir="ltr">{request.specDoc.name}</span>
          <Icon name="download" size={16} className="shrink-0" />
        </button>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="btn-secondary btn-sm gap-1.5"
        aria-expanded={open}
      >
        <Icon name="expand_more" size={16} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
        {open ? t.vendorPortal.closeForm : t.vendorPortal.fillQuote}
      </button>

      {open && <SubmitForm request={request} vendorName={vendorName} />}
    </article>
  );
}

export function VendorOpenRequests(): JSX.Element {
  const vendor = useVendorAuthStore((s) => s.vendor);
  const { data: requests = [], isLoading, isError } = useQuery({
    queryKey: ['vendor-open-requests'],
    queryFn: () => vendorPortalApi.openRequests(),
  });

  // Title/ref search, debounced so we don't filter per keystroke.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Page resets whenever the search changes (the result set changes).
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const filtered = useMemo(() => requests.filter((r) => {
    if (debouncedSearch) {
      const hay = `${r.title} ${r.refNumber}`.toLowerCase();
      if (!hay.includes(debouncedSearch)) return false;
    }
    return true;
  }), [requests, debouncedSearch]);

  const total = filtered.length;
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="page space-y-6">
      <header>
        <h1 className="font-serif text-3xl tracking-tight">{t.vendorPortal.openTitle}</h1>
      </header>

      {/* Toolbar — search only (no status filter; these are always "open"). Mirrors the staff list pattern. */}
      <div className="rounded-3xl bg-[hsl(var(--chrome))] flex flex-wrap items-center justify-end gap-x-6 gap-y-3 p-3 sm:px-4">
        <div className="relative w-full sm:w-72">
          <Icon name="search" size={18} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.vendorPortal.searchPlaceholder}
            className="input rounded-full py-1.5 ps-9 pe-4"
          />
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">{t.vendorPortal.loading}</p>
      ) : isError ? (
        <p className="card-paper p-6 text-center text-sm text-destructive">{t.vendorPortal.loadError}</p>
      ) : (
        <section className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
          {pageItems.map((r) => (
            <RequestCard key={r.requestId} request={r} vendorName={vendor?.name ?? ''} />
          ))}
          {total === 0 && (
            <p className="p-8 text-center text-sm text-muted-foreground">{t.vendorPortal.openEmpty}</p>
          )}
        </section>
      )}

      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
    </div>
  );
}
